import { useMemo } from 'react';
import { FPAData } from '../../../../infra/protected/FPA/FPAData';
import { getMaxExpandedDepth } from '../utils/treeUtils';

export interface DynamicIndentationResult {
  indentValue: number;
  maxDepth: number;
  isCompactMode: boolean;
}

/**
 * Custom hook that calculates dynamic indentation values based on tree depth and container width.
 * 
 * Formula: indentValue = max(3px, min(20px, containerWidth / (maxDepth * 5)))
 * 
 * Compact mode is activated when maxDepth > 4 AND containerWidth < 400px
 * 
 * @param treeData - Flat array of FPAData items representing the visible expanded tree
 * @param containerWidth - Current width of the container in pixels
 * @returns Object containing indentValue, maxDepth, and isCompactMode flag
 */
export function useDynamicIndentation(
  treeData: FPAData[],
  containerWidth: number
): DynamicIndentationResult {
  return useMemo(() => {
    const maxDepth = getMaxExpandedDepth(treeData);
    
    // Calculate dynamic indentation value
    // As depth increases, available space per level decreases proportionally
    const calculatedIndent = containerWidth > 0 && maxDepth > 0
      ? containerWidth / (maxDepth * 5)
      : 20; // Default to 20px if calculation can't be performed
    
    // Constrain between minimum (3px) and maximum (20px)
    let indentValue = Math.max(3, Math.min(20, calculatedIndent));
    
    // Compact mode: triggered when depth > 4 AND container width < 400px
    const isCompactMode = maxDepth > 4 && containerWidth < 400;
    
    // In compact mode, force indentation to strict 3px minimum
    if (isCompactMode) {
      indentValue = 3;
    }
    
    return {
      indentValue,
      maxDepth,
      isCompactMode
    };
  }, [treeData, containerWidth]);
}
